import React, { useState } from 'react';
import { GripVertical, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutPicker } from './LayoutPicker';
import { CardItem } from './CardItem';
import { UrlFetcher } from './UrlFetcher';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface EditorSection {
  id: string;
  type: 'listings-videos' | 'custom-links';
  title: string;
  description: string;
  layout: string;
  cards: EditorCard[];
  collapsed: boolean;
}
interface SectionCardProps {
  section: EditorSection;
  onUpdate: (id: string, updates: Partial<EditorSection>) => void;
  onDelete: (id: string) => void;
  onCardUpdate: (sectionId: string, cardId: string, updates: Partial<EditorCard>) => void;
  onCardDelete: (sectionId: string, cardId: string) => void;
  onCardAdd: (sectionId: string, card: Omit<EditorCard, 'id' | 'enabled'>) => void;
  onCardReorder: (sectionId: string, fromIndex: number, toIndex: number) => void;
  onDragStart: (e: React.DragEvent, sectionId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetSectionId: string) => void;
  isDragging: boolean;
}
export function SectionCard({
  section,
  onUpdate,
  onDelete,
  onCardUpdate,
  onCardDelete,
  onCardAdd,
  onCardReorder,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging
}: SectionCardProps) {
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggedCardId(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleCardDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleCardDrop = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedCardId || draggedCardId === targetCardId) {
      setDraggedCardId(null);
      return;
    }
    const fromIndex = section.cards.findIndex(c => c.id === draggedCardId);
    const toIndex = section.cards.findIndex(c => c.id === targetCardId);
    if (fromIndex !== -1 && toIndex !== -1) {
      onCardReorder(section.id, fromIndex, toIndex);
    }
    setDraggedCardId(null);
  };
  const handleAddManualCard = () => {
    onCardAdd(section.id, {
      title: 'New Link',
      url: 'https://example.com',
      description: 'Add a description',
      image: ''
    });
    setShowManualAdd(false);
  };
  return <div draggable onDragStart={e => onDragStart(e, section.id)} onDragOver={onDragOver} onDrop={e => onDrop(e, section.id)} className={`bg-white rounded-2xl border border-brand-light shadow-sm transition-all ${isDragging ? 'opacity-50' : ''}`} data-id="element-157">
      <div className="p-6" data-id="element-158">
        <div className="flex items-start gap-3 mb-4" data-id="element-159">
          <button type="button" className="mt-2 text-brand-light hover:text-brand-muted cursor-grab active:cursor-grabbing transition-colors" data-id="element-160">
            <GripVertical size={20} data-id="element-161" />
          </button>

          <div className="flex-1 space-y-2" data-id="element-162">
            <div className="flex items-center gap-2" data-id="element-163">
              <input type="text" value={section.title} onChange={e => onUpdate(section.id, {
              title: e.target.value
            })} className="flex-1 text-lg font-medium text-brand-dark outline-none border-b-2 border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Section title" data-id="element-164" />
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-brand-sage text-brand-forest border border-brand-sage" data-id="element-165">
                {section.type === 'listings-videos' ? 'Listings & Videos' : 'Custom Links'}
              </span>
            </div>
            <input type="text" value={section.description} onChange={e => onUpdate(section.id, {
            description: e.target.value
          })} className="w-full text-sm text-brand-muted outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Section description" data-id="element-166" />
          </div>

          <div className="flex items-center gap-1 shrink-0" data-id="element-167">
            <button type="button" onClick={() => onUpdate(section.id, {
            collapsed: !section.collapsed
          })} className="p-2 text-brand-muted hover:text-brand-dark hover:bg-brand-offwhite rounded-lg transition-colors" data-id="element-168">
              {section.collapsed ? <ChevronDown size={18} data-id="element-169" /> : <ChevronUp size={18} data-id="element-170" />}
            </button>
            <button type="button" onClick={() => onDelete(section.id)} className="p-2 text-brand-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-171">
              <Trash2 size={18} data-id="element-172" />
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-173">
          {!section.collapsed && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-174">
              <div className="border-t border-brand-light pt-4 space-y-4" data-id="element-175">
                <LayoutPicker sectionType={section.type} selectedLayout={section.layout} onLayoutChange={layout => onUpdate(section.id, {
              layout
            })} data-id="element-176" />

                <div className="space-y-3" data-id="element-177">
                  {section.cards.map(card => <CardItem key={card.id} card={card} sectionType={section.type} onUpdate={(cardId, updates) => onCardUpdate(section.id, cardId, updates)} onDelete={cardId => onCardDelete(section.id, cardId)} onDragStart={handleCardDragStart} onDragOver={handleCardDragOver} onDrop={handleCardDrop} isDragging={draggedCardId === card.id} data-id="element-178" />)}
                </div>

                <div className="space-y-3 pt-2" data-id="element-179">
                  <UrlFetcher sectionType={section.type} onCardAdd={cardData => onCardAdd(section.id, cardData)} data-id="element-180" />
                  <button type="button" onClick={handleAddManualCard} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-181">
                    or add manually
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}